'use client';
import React from 'react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';

interface WpmTimelineChartProps {
  timeline: number[];
  duration: number;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ value: number }>;
  label?: string;
}

function CustomTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload?.length) return null;
  return (
    <div className="layer-float px-3 py-2">
      <div className="eyebrow">Second {label}</div>
      <div className="font-mono text-sm text-foreground mt-1 stat-value">
        {payload[0].value} WPM
      </div>
    </div>
  );
}

export default function WpmTimelineChart({ timeline, duration }: WpmTimelineChartProps) {
  const data =
    timeline.length > 0
      ? timeline.map((wpm, i) => ({ second: i + 1, wpm }))
      : Array.from({ length: 12 }, (_, i) => ({
          second: (i + 1) * Math.floor(duration / 12),
          wpm: 0,
        }));

  const peak = timeline.length ? Math.max(...timeline) : 0;

  return (
    <div className="card-elevated p-5 sm:p-6 h-full">
      <div className="flex items-baseline justify-between gap-3 mb-5">
        <span className="eyebrow">Speed over time</span>
        {peak > 0 && (
          <span className="text-xs text-faint">
            peak <span className="font-mono text-muted-foreground">{peak}</span> wpm
          </span>
        )}
      </div>

      {timeline.length === 0 ? (
        <div className="flex items-center justify-center h-[180px] text-sm text-faint">
          No timeline data available
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={190}>
          <AreaChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
            <defs>
              {/* A whisper of tone under the line — never a coloured wash */}
              <linearGradient id="wpmFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--foreground)" stopOpacity={0.07} />
                <stop offset="100%" stopColor="var(--foreground)" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="var(--border)" vertical={false} />
            <XAxis
              dataKey="second"
              tick={{ fontSize: 11, fill: 'var(--faint)' }}
              axisLine={{ stroke: 'var(--border)' }}
              tickLine={false}
              tickFormatter={(v) => `${v}s`}
            />
            <YAxis
              tick={{ fontSize: 11, fill: 'var(--faint)' }}
              axisLine={false}
              tickLine={false}
              width={38}
              tickMargin={6}
            />
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ stroke: 'var(--border-strong)', strokeWidth: 1 }}
            />
            <Area
              type="monotone"
              dataKey="wpm"
              stroke="var(--foreground)"
              strokeWidth={1.5}
              fill="url(#wpmFill)"
              dot={false}
              activeDot={{ r: 3, fill: 'var(--foreground)', strokeWidth: 0 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
